import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, LineChart, Area, ComposedChart, Cell, Scatter } from 'recharts';
import {
  calculateAverage,
  calculateTrend,
  getBestWorstFingers,
  getMostInconsistentFingers,
  prepareTrendOverTimeData,
  prepareTrendOverTempoData,
  prepareTrendOverRepetitionData,
  calculateLinearRegression,
  filterSessionsByArticulationType
} from '../../utils/articulationUtils';

// Helper to calculate standard deviation
const calculateStdDev = (values) => {
  if (values.length === 0) return 0;
  const mean = values.reduce((acc, val) => acc + val, 0) / values.length;
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((acc, val) => acc + val, 0) / values.length;
  return Math.sqrt(variance);
};

// Helper to calculate Y-axis range based on actual data
const getDurationYDomain = (regressionData, scatterData = {}) => {
  if (!regressionData || regressionData.length === 0) return [-20, 20];
  
  let maxAbs = 0;
  
  // Check regression data with std dev
  regressionData.forEach(series => {
    series.data.forEach(point => {
      const upper = Math.abs(point.upper || point.value);
      const lower = Math.abs(point.lower || point.value);
      maxAbs = Math.max(maxAbs, upper, lower);
    });
  });
  
  // Check scatter data
  Object.values(scatterData).forEach(data => {
    if (data.points) {
      data.points.forEach(point => {
        maxAbs = Math.max(maxAbs, Math.abs(point.y));
      });
    }
  });
  
  // Add 20% padding
  const rangeWithPadding = maxAbs * 1.2;
  
  // Round to nice numbers
  let range;
  if (rangeWithPadding <= 5) {
    range = Math.ceil(rangeWithPadding);
  } else if (rangeWithPadding <= 10) {
    range = Math.ceil(rangeWithPadding / 2) * 2;
  } else if (rangeWithPadding <= 20) {
    range = Math.ceil(rangeWithPadding / 5) * 5;
  } else if (rangeWithPadding <= 50) {
    range = Math.ceil(rangeWithPadding / 10) * 10;
  } else {
    range = Math.ceil(rangeWithPadding / 20) * 20;
  }
  
  return [-range, range];
};

// Helper to generate Y-axis ticks that are symmetrical around 0
const generateDurationTicks = (domain) => {
  const [min, max] = domain;
  const range = max - min;
  
  let step;
  if (range <= 10) {
    step = 2;
  } else if (range <= 20) {
    step = 5;
  } else if (range <= 50) {
    step = 10;
  } else if (range <= 100) {
    step = 20;
  } else {
    step = 50;
  }
  
  const ticks = [0];
  
  for (let i = step; i <= max; i += step) {
    ticks.push(i);
  }
  
  for (let i = -step; i >= min; i -= step) {
    ticks.unshift(i);
  }
  
  return ticks;
};

const ArticulationOverview = ({ sessions, filteredSessions, timeMode }) => {
  // Calculate overall duration delta
  const overallDurationDelta = timeMode === 'trend'
    ? calculateTrend(filteredSessions, 'overall_relative_duration_avg')
    : { simple: calculateAverage(filteredSessions, 'overall_relative_duration_avg') };

  // Get worst offenders (longest/shortest duration deltas)
  const leftFingers = getBestWorstFingers(filteredSessions, 'left');
  const rightFingers = getBestWorstFingers(filteredSessions, 'right');
  
  // Get most inconsistent fingers (highest std dev)
  const leftInconsistent = getMostInconsistentFingers(filteredSessions, 'left');
  const rightInconsistent = getMostInconsistentFingers(filteredSessions, 'right');

  // Prepare data for charts (always use all sessions for trend data)
  // Show longest/shortest and most inconsistent fingers
  const leftFingersToShow = [leftFingers.longest?.finger, leftFingers.shortest?.finger, leftInconsistent.mostInconsistent?.finger].filter(Boolean);
  const rightFingersToShow = [rightFingers.longest?.finger, rightFingers.shortest?.finger, rightInconsistent.mostInconsistent?.finger].filter(Boolean);
  
  const leftTrendTime = prepareTrendOverTimeData(
    sessions,
    'left',
    leftFingersToShow
  );
  const rightTrendTime = prepareTrendOverTimeData(
    sessions,
    'right',
    rightFingersToShow
  );

  const leftTrendTempo = prepareTrendOverTempoData(
    sessions,
    'left',
    leftFingersToShow
  );
  const rightTrendTempo = prepareTrendOverTempoData(
    sessions,
    'right',
    rightFingersToShow
  );

  const leftTrendRepetition = prepareTrendOverRepetitionData(
    sessions,
    'left',
    leftFingersToShow
  );
  const rightTrendRepetition = prepareTrendOverRepetitionData(
    sessions,
    'right',
    rightFingersToShow
  );

  // Calculate articulation type distribution
  const articulationTypeDistribution = useMemo(() => {
    const distribution = {
      staccato: 0,
      legato: 0,
      portato: 0,
      unknown: 0
    };
    
    filteredSessions.forEach(s => {
      const type = s.articulation_type || 'unknown';
      distribution[type] = (distribution[type] || 0) + 1;
    });
    
    return Object.entries(distribution)
      .map(([type, count]) => ({
        type: type.charAt(0).toUpperCase() + type.slice(1),
        count,
        percentage: (count / filteredSessions.length) * 100
      }))
      .filter(d => d.count > 0);
  }, [filteredSessions]);

  // Colors
  const purpleShades = ['#6b21a8', '#9333ea', '#a855f7', '#c084fc'];
  const orangeShades = ['#ea580c', '#f97316', '#fb923c', '#fdba74']; // More orange, less red
  const distributionColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

  // Create regression lines for time chart
  const timeRegressionData = [];
  Object.entries(leftTrendTime).forEach(([finger, data], idx) => {
    if (data.regression && data.points.length > 0) {
      const values = data.points.map(p => p.y);
      const stdDev = calculateStdDev(values);
      const minX = Math.min(...data.points.map(p => p.x));
      const maxX = Math.max(...data.points.map(p => p.x));
      
      const points = [];
      const step = (maxX - minX) / 20;
      for (let x = minX; x <= maxX; x += step) {
        const y = data.regression.slope * x + data.regression.intercept;
        points.push({
          timestamp: x,
          value: y, // No clamping for duration delta
          upper: y + stdDev,
          lower: y - stdDev
        });
      }
      
      timeRegressionData.push({
        finger: `L${finger}`,
        color: purpleShades[idx % purpleShades.length],
        data: points,
        stdDev
      });
    }
  });
  
  Object.entries(rightTrendTime).forEach(([finger, data], idx) => {
    if (data.regression && data.points.length > 0) {
      const values = data.points.map(p => p.y);
      const stdDev = calculateStdDev(values);
      const minX = Math.min(...data.points.map(p => p.x));
      const maxX = Math.max(...data.points.map(p => p.x));
      
      const points = [];
      const step = (maxX - minX) / 20;
      for (let x = minX; x <= maxX; x += step) {
        const y = data.regression.slope * x + data.regression.intercept;
        points.push({
          timestamp: x,
          value: y,
          upper: y + stdDev,
          lower: y - stdDev
        });
      }
      
      timeRegressionData.push({
        finger: `R${finger}`,
        color: orangeShades[idx % orangeShades.length],
        data: points,
        stdDev
      });
    }
  });
  
  // Create regression lines for tempo chart
  const tempoRegressionData = [];
  Object.entries(leftTrendTempo).forEach(([finger, data], idx) => {
    if (data.points.length > 1) {
      const regression = calculateLinearRegression(data.points);
      if (regression) {
        const values = data.points.map(p => p.y);
        const stdDev = calculateStdDev(values);
        const minX = Math.min(...data.points.map(p => p.x));
        const maxX = Math.max(...data.points.map(p => p.x));
        
        const points = [];
        const step = (maxX - minX) / 20;
        for (let x = minX; x <= maxX; x += step) {
          const y = regression.slope * x + regression.intercept;
          points.push({
            tempo: x,
            value: y,
            upper: y + stdDev,
            lower: y - stdDev
          });
        }
        
        tempoRegressionData.push({
          finger: `L${finger}`,
          color: purpleShades[idx % purpleShades.length],
          data: points,
          stdDev
        });
      }
    }
  });
  
  Object.entries(rightTrendTempo).forEach(([finger, data], idx) => {
    if (data.points.length > 1) {
      const regression = calculateLinearRegression(data.points);
      if (regression) {
        const values = data.points.map(p => p.y);
        const stdDev = calculateStdDev(values);
        const minX = Math.min(...data.points.map(p => p.x));
        const maxX = Math.max(...data.points.map(p => p.x));
        
        const points = [];
        const step = (maxX - minX) / 20;
        for (let x = minX; x <= maxX; x += step) {
          const y = regression.slope * x + regression.intercept;
          points.push({
            tempo: x,
            value: y,
            upper: y + stdDev,
            lower: y - stdDev
          });
        }
        
        tempoRegressionData.push({
          finger: `R${finger}`,
          color: orangeShades[idx % orangeShades.length],
          data: points,
          stdDev
        });
      }
    }
  });

  // Create regression lines for repetition chart
  const repetitionRegressionData = [];
  Object.entries(leftTrendRepetition).forEach(([finger, data], idx) => {
    if (data.regression && data.points.length > 0) {
      const values = data.points.map(p => p.y);
      const stdDev = calculateStdDev(values);
      const minX = Math.min(...data.points.map(p => p.x));
      const maxX = Math.max(...data.points.map(p => p.x));
      
      const points = [];
      const step = Math.max(1, (maxX - minX) / 20);
      for (let x = minX; x <= maxX; x += step) {
        const y = data.regression.slope * x + data.regression.intercept;
        points.push({
          repetition: x,
          value: y,
          upper: y + stdDev,
          lower: y - stdDev
        });
      }
      
      repetitionRegressionData.push({
        finger: `L${finger}`,
        color: purpleShades[idx % purpleShades.length],
        data: points,
        stdDev
      });
    }
  });
  
  Object.entries(rightTrendRepetition).forEach(([finger, data], idx) => {
    if (data.regression && data.points.length > 0) {
      const values = data.points.map(p => p.y);
      const stdDev = calculateStdDev(values);
      const minX = Math.min(...data.points.map(p => p.x));
      const maxX = Math.max(...data.points.map(p => p.x));
      
      const points = [];
      const step = Math.max(1, (maxX - minX) / 20);
      for (let x = minX; x <= maxX; x += step) {
        const y = data.regression.slope * x + data.regression.intercept;
        points.push({
          repetition: x,
          value: y,
          upper: y + stdDev,
          lower: y - stdDev
        });
      }
      
      repetitionRegressionData.push({
        finger: `R${finger}`,
        color: orangeShades[idx % orangeShades.length],
        data: points,
        stdDev
      });
    }
  });

  // Custom legend component
  const renderCustomLegend = (data) => {
    return (
      <div className="flex flex-col gap-1 text-[9px]">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-center gap-1">
            <div className="w-2 h-0.5" style={{ backgroundColor: item.color }}></div>
            <span>{item.finger}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex gap-3">
      {/* Left Column: Overall Duration Delta (top) & Articulation Types (bottom) */}
      <div className="flex flex-col gap-3" style={{ width: '200px' }}>
        {/* Overall Duration Delta */}
        <div className="bg-gray-50 p-2 rounded">
          <div className="text-xs font-semibold text-gray-600 mb-1">Overall Duration Δ</div>
          {timeMode === 'trend' ? (
            <div className="text-[10px] space-y-0.5">
              <div className="flex justify-between">
                <span className="text-gray-600">5d:</span>
                <span className="font-medium">{overallDurationDelta.last5?.toFixed(1) || 'N/A'}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">30d:</span>
                <span className="font-medium">{overallDurationDelta.last30?.toFixed(1) || 'N/A'}%</span>
              </div>
              <div className="flex justify-between pt-0.5 border-t border-gray-300">
                <span className="text-gray-600 font-semibold">Δ:</span>
                <span className={`font-bold ${Math.abs(overallDurationDelta.difference) < Math.abs(overallDurationDelta.last30) ? 'text-green-600' : 'text-red-600'}`}>
                  {overallDurationDelta.difference > 0 ? '+' : ''}{overallDurationDelta.difference?.toFixed(1) || 'N/A'}%
                </span>
              </div>
            </div>
          ) : (
            <p className="text-xs font-medium">
              {overallDurationDelta.simple?.toFixed(1) || 'N/A'}%
            </p>
          )}
        </div>

        {/* Articulation Type Distribution */}
        <div className="bg-gray-50 p-2 rounded flex-1">
          <div className="text-xs font-semibold text-gray-600 mb-1">Articulation Types</div>
          {articulationTypeDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={80}>
              <BarChart data={articulationTypeDistribution} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                <XAxis dataKey="type" tick={{ fontSize: 8 }} />
                <YAxis tick={{ fontSize: 8 }} width={25} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white p-1 border border-gray-300 rounded shadow-sm">
                          <p className="text-[9px] font-semibold">{data.type}</p>
                          <p className="text-[9px]">Count: {data.count}</p>
                          <p className="text-[9px]">{data.percentage.toFixed(1)}%</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {articulationTypeDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={distributionColors[index % distributionColors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-[9px] text-gray-500 text-center py-4">No data</p>
          )}
        </div>
      </div>

      {/* Second Column: Worst Offenders (top) & Least Consistent (bottom) */}
      <div className="flex flex-col gap-3" style={{ width: '220px' }}>
        {/* Worst Offenders */}
        <div className="bg-gray-50 p-2 rounded">
          <div className="text-xs font-semibold text-gray-600 mb-1">Worst Offenders</div>
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b border-gray-300">
                <th className="text-left py-0.5"></th>
                <th className="text-center py-0.5 font-semibold" style={{ color: '#9333ea' }}>Left</th>
                <th className="text-center py-0.5 font-semibold" style={{ color: '#ea580c' }}>Right</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-0.5 font-semibold text-gray-600">Long</td>
                <td className="text-center py-0.5">
                  <span className="font-bold" style={{ color: '#9333ea' }}>
                    {leftFingers.longest?.finger || '-'}
                  </span>
                  <span className="text-gray-500 ml-1 text-[9px]">
                    ({leftFingers.longest?.avg?.toFixed(1) || 'N/A'}%)
                  </span>
                </td>
                <td className="text-center py-0.5">
                  <span className="font-bold" style={{ color: '#ea580c' }}>
                    {rightFingers.longest?.finger || '-'}
                  </span>
                  <span className="text-gray-500 ml-1 text-[9px]">
                    ({rightFingers.longest?.avg?.toFixed(1) || 'N/A'}%)
                  </span>
                </td>
              </tr>
              <tr>
                <td className="py-0.5 font-semibold text-gray-600">Short</td>
                <td className="text-center py-0.5">
                  <span className="font-bold" style={{ color: '#9333ea' }}>
                    {leftFingers.shortest?.finger || '-'}
                  </span>
                  <span className="text-gray-500 ml-1 text-[9px]">
                    ({leftFingers.shortest?.avg?.toFixed(1) || 'N/A'}%)
                  </span>
                </td>
                <td className="text-center py-0.5">
                  <span className="font-bold" style={{ color: '#ea580c' }}>
                    {rightFingers.shortest?.finger || '-'}
                  </span>
                  <span className="text-gray-500 ml-1 text-[9px]">
                    ({rightFingers.shortest?.avg?.toFixed(1) || 'N/A'}%)
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Least Consistent */}
        <div className="bg-gray-50 p-2 rounded flex-1">
          <div className="text-xs font-semibold text-gray-600 mb-1">Least Consistent</div>
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b border-gray-300">
                <th className="text-left py-0.5">Hand</th>
                <th className="text-center py-0.5 font-semibold">Finger</th>
                <th className="text-center py-0.5 font-semibold">Std Dev</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-0.5 font-semibold" style={{ color: '#9333ea' }}>Left</td>
                <td className="text-center py-0.5">
                  <span className="font-bold" style={{ color: '#9333ea' }}>
                    {leftInconsistent.mostInconsistent?.finger || '-'}
                  </span>
                </td>
                <td className="text-center py-0.5 text-gray-600">
                  {leftInconsistent.mostInconsistent?.stdDev?.toFixed(2) || 'N/A'}
                </td>
              </tr>
              <tr>
                <td className="py-0.5 font-semibold" style={{ color: '#ea580c' }}>Right</td>
                <td className="text-center py-0.5">
                  <span className="font-bold" style={{ color: '#ea580c' }}>
                    {rightInconsistent.mostInconsistent?.finger || '-'}
                  </span>
                </td>
                <td className="text-center py-0.5 text-gray-600">
                  {rightInconsistent.mostInconsistent?.stdDev?.toFixed(2) || 'N/A'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Third Column: Performance over Tempo (full height) */}
      <div className="bg-gray-50 p-2 rounded flex-1">
          <h3 className="text-[10px] font-semibold text-gray-700 mb-1">Performance over tempo</h3>
          {(leftTrendTempo && Object.keys(leftTrendTempo).length > 0) || (rightTrendTempo && Object.keys(rightTrendTempo).length > 0) ? (
            (() => {
              const combinedScatterData = { ...leftTrendTempo, ...rightTrendTempo };
              const tempoDomain = getDurationYDomain(tempoRegressionData, combinedScatterData);
              const tempoTicks = generateDurationTicks(tempoDomain);
              
              return (
                <div className="flex gap-1">
                  <ResponsiveContainer width="80%" height={120}>
                    <ComposedChart margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                      <XAxis 
                        type="number"
                        dataKey="x" 
                        domain={['dataMin', 'dataMax']}
                        hide
                      />
                      <YAxis 
                        type="number"
                        domain={tempoDomain}
                        ticks={tempoTicks}
                        tick={{ fontSize: 8 }}
                        width={25}
                      />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-1 border border-gray-300 rounded shadow-sm">
                            <p className="text-[9px]">BPM: {Math.round(data.x)}</p>
                            <p className="text-[9px]">Δ: {data.y?.toFixed(1)}%</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Scatter plots for actual data */}
                  {Object.entries(leftTrendTempo).map(([finger, data], idx) => (
                    <Scatter
                      key={`left-scatter-${finger}`}
                      data={data.points}
                      dataKey="y"
                      fill={purpleShades[idx % purpleShades.length]}
                      fillOpacity={0.6}
                      r={3}
                      isAnimationActive={false}
                    />
                  ))}
                  {Object.entries(rightTrendTempo).map(([finger, data], idx) => (
                    <Scatter
                      key={`right-scatter-${finger}`}
                      data={data.points}
                      dataKey="y"
                      fill={orangeShades[idx % orangeShades.length]}
                      fillOpacity={0.6}
                      r={3}
                      isAnimationActive={false}
                    />
                  ))}
                  {/* Regression lines */}
                  {tempoRegressionData.map((regData, idx) => (
                    <Line
                      key={`regression-${idx}`}
                      data={regData.data.map(d => ({ x: d.tempo, y: d.value }))}
                      type="monotone"
                      dataKey="y"
                      stroke={regData.color}
                      strokeWidth={1.5}
                      dot={false}
                      isAnimationActive={false}
                    />
                  ))}
                    </ComposedChart>
                  </ResponsiveContainer>
                  {renderCustomLegend(tempoRegressionData)}
                </div>
              );
            })()
          ) : (
            <p className="text-[9px] text-gray-500 text-center py-4">No data</p>
          )}
      </div>

      {/* Fourth Column: Performance by Repetition (full height) */}
      <div className="bg-gray-50 p-2 rounded flex-1">
        <h3 className="text-[10px] font-semibold text-gray-700 mb-1">Performance by repetition</h3>
        {(leftTrendRepetition && Object.keys(leftTrendRepetition).length > 0) || (rightTrendRepetition && Object.keys(rightTrendRepetition).length > 0) ? (
          (() => {
            const combinedScatterData = { ...leftTrendRepetition, ...rightTrendRepetition };
            const repetitionDomain = getDurationYDomain(repetitionRegressionData, combinedScatterData);
            const repetitionTicks = generateDurationTicks(repetitionDomain);
            
            return (
              <div className="flex gap-1">
                <ResponsiveContainer width="80%" height={120}>
                  <ComposedChart margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                    <XAxis 
                      type="number"
                      dataKey="x" 
                      domain={['dataMin', 'dataMax']}
                      hide
                    />
                    <YAxis 
                      type="number"
                      domain={repetitionDomain}
                      ticks={repetitionTicks}
                      tick={{ fontSize: 8 }}
                      width={25}
                    />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white p-1 border border-gray-300 rounded shadow-sm">
                          <p className="text-[9px]">Rep: #{Math.round(data.x)}</p>
                          <p className="text-[9px]">Δ: {data.y?.toFixed(1)}%</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                    {/* Scatter plots for actual data */}
                    {Object.entries(leftTrendRepetition).map(([finger, data], idx) => (
                      <Scatter
                        key={`left-scatter-${finger}`}
                        data={data.points}
                        dataKey="y"
                        fill={purpleShades[idx % purpleShades.length]}
                        fillOpacity={0.6}
                        r={3}
                        isAnimationActive={false}
                      />
                    ))}
                    {Object.entries(rightTrendRepetition).map(([finger, data], idx) => (
                      <Scatter
                        key={`right-scatter-${finger}`}
                        data={data.points}
                        dataKey="y"
                        fill={orangeShades[idx % orangeShades.length]}
                        fillOpacity={0.6}
                        r={3}
                        isAnimationActive={false}
                      />
                    ))}
                    {/* Regression lines */}
                    {repetitionRegressionData.map((regData, idx) => (
                      <Line
                        key={`regression-${idx}`}
                        data={regData.data.map(d => ({ x: d.repetition, y: d.value }))}
                        type="monotone"
                        dataKey="y"
                        stroke={regData.color}
                        strokeWidth={1.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
                {renderCustomLegend(repetitionRegressionData)}
              </div>
            );
          })()
        ) : (
          <p className="text-[9px] text-gray-500 text-center py-4">No data</p>
        )}
      </div>
    </div>
  );
};

export default ArticulationOverview;

